import { IAppState } from './../../store/state/app.state';
import { PercentagePipe } from 'src/app/utils/percentage.pipe';
import { DatePipe, DecimalPipe } from '@angular/common';
import { BaseXLSReportService } from './base.excel-report.service';
import { Store } from '@ngrx/store';
import { Injectable } from "@angular/core";
import * as Excel from "exceljs";
import { ICashflowAvailableData, ICashflowCouponsData } from 'src/app/models/cashflow';

export interface CurrencyTotalsVM {
    [name: string]: {
        currencyName: string,
        totalCurrencyCashBalance,
        totalCurrencyUnsettledTrades,
        totalCurrencyOutgoingsPending,
        totalCurrencyCashAvailable,
        totalCurrencyIncomePending
    };
}

@Injectable()
export class CashflowExcelReportService extends BaseXLSReportService {
  constructor(
    public store: Store<IAppState>,
    public decimalPipe: DecimalPipe,
    public percentagePipe: PercentagePipe,
    public datePipe: DatePipe
  ) {
    super(store, decimalPipe, percentagePipe, datePipe);
  }

  exportCashflowToExcel(cashflowCouponsData?: ICashflowCouponsData[], cashAvailableData?: ICashflowAvailableData) {
    const workbook: Excel.Workbook = new Excel.Workbook();
    // this is a workaround: https://github.com/guyonroche/exceljs/issues/348#issuecomment-320690232
    workbook.creator = 'Adminis';
    workbook.created = new Date();
    workbook.modified = new Date();

    if (
      !!cashAvailableData &&
      cashAvailableData.children.length > 0
    ) {
      const cashAvailableSheet: Excel.Worksheet =
        workbook.addWorksheet('Cash Available');
      this.createCashAvailableSheet(cashAvailableSheet, cashAvailableData);
    }

    if (cashflowCouponsData !== undefined && cashflowCouponsData.length > 0) {
      const couponsSheet: Excel.Worksheet = workbook.addWorksheet(
        'Coupons and Maturities'
      );
      this.createCouponsSheet(couponsSheet, cashflowCouponsData);
    }

    const date = this.datePipe.transform(
      new Date(this.selectedDate),
      'd MMM yyyy'
    );
    const reportFileName =
      this.selectedEntities[0].Name + '_Cashflow_' + date + '.xlsx';
    this.writeSheet(workbook, reportFileName);
  }

  createCashAvailableSheet(worksheet: Excel.Worksheet, cashflowAvailableData: ICashflowAvailableData) {
    let rowIndex = 0;

    worksheet.columns = [
      { header: 'Cash', key: '', width: 40 },
      { header: '', key: '', width: 40 },
      { header: '', key: '', width: 20 },
      { header: '', key: '', width: 20 },
      { header: '', key: '', width: 20 },
      { header: '', key: '', width: 20 },
      { header: '', key: '', width: 20 },
    ];

    const row = worksheet.getRow(1);
    row.font = {
      name: 'Segoe UI',
      family: 2,
      size: 9,
      underline: false,
      bold: true,
      color: { argb: '000000' },
    };

    rowIndex++;

    worksheet.addRow([
      'Portfolio',
      'Currency',
      'Cash Balances',
      'Unsettled Trades',
      'Expenses Pending',
      'Cash Available',
      'Income Pending',
    ]).font = {
      name: 'Segoe UI',
      family: 2,
      size: 9,
      underline: false,
      bold: false,
      color: { argb: '000000' },
    };

    rowIndex++;

    for (let i = 0; i < worksheet.columns.length; i++) {
      const cell = worksheet.getCell(this.xlsColumns[i] + rowIndex);
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: '80BD01' },
        bgColor: { argb: '80BD01' },
      };

      cell.font = {
        name: 'Segoe UI',
        family: 2,
        size: 9,
        underline: false,
        bold: true,
        color: { argb: 'ffffff' },
      };
    }

    this.createCashAvailableTable(worksheet, rowIndex, cashflowAvailableData);
  }

  createCashAvailableTable(worksheet: Excel.Worksheet, rowIndex: number, cashAvailableData: ICashflowAvailableData) {
    const currencyTotals: CurrencyTotalsVM = {};
    const availableCashflowEntities = cashAvailableData.children;
    for (let i = 0; i < availableCashflowEntities.length; i++) {
      const cashFlowAvailableEntity = availableCashflowEntities[i];
      const entityCashBalance = cashFlowAvailableEntity.data.CashBalances;
      const entityUnsettledTrades =
        cashFlowAvailableEntity.data.UnsettledTrades;
      const entityOutgoingsPending =
        cashFlowAvailableEntity.data.OutgoingsPending;
      const entityCashAvailable = cashFlowAvailableEntity.data.CashAvailable;
      const entityIncomePending = cashFlowAvailableEntity.data.IncomePending;

      worksheet.addRow([
        cashFlowAvailableEntity.data.Caption,
        'Total',
        entityCashBalance,
        entityUnsettledTrades,
        entityOutgoingsPending,
        entityCashAvailable,
        entityIncomePending,
      ]).font = {
        name: 'Segoe UI',
        family: 2,
        size: 9,
        underline: false,
        bold: true,
        color: { argb: '000000' },
      };

      rowIndex++;

      const entityCashflowAvailableChildrenData =
        cashFlowAvailableEntity.children;
      for (let j = 0; j < entityCashflowAvailableChildrenData.length; j++) {
        const entityChildAvailableCashflow =
          entityCashflowAvailableChildrenData[j];
        const entityChildCashBalance =
          entityChildAvailableCashflow.data.CashBalances;
        const entityChildUnsettledTrades =
          entityChildAvailableCashflow.data.UnsettledTrades;
        const entityChildOutgoingsPending =
          entityChildAvailableCashflow.data.OutgoingsPending;
        const entityChildCashAvailable =
          entityChildAvailableCashflow.data.CashAvailable;
        const entityChildIncomePending =
          entityChildAvailableCashflow.data.IncomePending;
        worksheet.addRow([
          cashFlowAvailableEntity.data.Caption,
          entityChildAvailableCashflow.data.Caption,
          entityChildCashBalance,
          entityChildUnsettledTrades,
          entityChildOutgoingsPending,
          entityChildCashAvailable,
          entityChildIncomePending,
        ]).font = {
          name: 'Segoe UI',
          family: 2,
          size: 9,
          underline: false,
          bold: false,
          color: { argb: '000000' },
        };

        if (i === 0) {
          currencyTotals[entityChildAvailableCashflow.data.Caption] = {
            currencyName: entityChildAvailableCashflow.data.Caption,
            totalCurrencyCashBalance: entityChildCashBalance,
            totalCurrencyUnsettledTrades: entityChildUnsettledTrades,
            totalCurrencyOutgoingsPending: entityChildOutgoingsPending,
            totalCurrencyCashAvailable: entityChildCashAvailable,
            totalCurrencyIncomePending: entityChildIncomePending,
          };
        } else {
          currencyTotals[
            entityChildAvailableCashflow.data.Caption
          ].currencyName =
            currencyTotals[
              entityChildAvailableCashflow.data.Caption
            ].currencyName;
          currencyTotals[
            entityChildAvailableCashflow.data.Caption
          ].totalCurrencyCashBalance =
            currencyTotals[entityChildAvailableCashflow.data.Caption]
              .totalCurrencyCashBalance + entityChildCashBalance;
          currencyTotals[
            entityChildAvailableCashflow.data.Caption
          ].totalCurrencyUnsettledTrades =
            currencyTotals[entityChildAvailableCashflow.data.Caption]
              .totalCurrencyUnsettledTrades + entityChildUnsettledTrades;
          currencyTotals[
            entityChildAvailableCashflow.data.Caption
          ].totalCurrencyOutgoingsPending =
            currencyTotals[entityChildAvailableCashflow.data.Caption]
              .totalCurrencyOutgoingsPending + entityChildOutgoingsPending;
          currencyTotals[
            entityChildAvailableCashflow.data.Caption
          ].totalCurrencyCashAvailable =
            currencyTotals[entityChildAvailableCashflow.data.Caption]
              .totalCurrencyCashAvailable + entityChildCashAvailable;
          currencyTotals[
            entityChildAvailableCashflow.data.Caption
          ].totalCurrencyIncomePending =
            currencyTotals[entityChildAvailableCashflow.data.Caption]
              .totalCurrencyIncomePending + entityChildIncomePending;
        }

        rowIndex++;
      }

      for (let j = 0; j < 7; j++) {
        const rowNumberString = rowIndex.toString();
        if (i !== availableCashflowEntities.length) {
          worksheet.getCell(this.xlsColumns[j] + rowNumberString).border = {
            bottom: { style: 'thin', color: { argb: '000000' } },
          };
        }
      }
    }

    this.createCashAvailableTotalsPerCurrency(
      worksheet,
      currencyTotals,
      rowIndex,
      cashAvailableData
    );
  }

  //pass data in here
  createCashAvailableTotalsPerCurrency(
    worksheet: Excel.Worksheet,
    currencyTotals: CurrencyTotalsVM,
    rowIndex: number,
    cashAvailableData: ICashflowAvailableData
  ) {
    const totalCashBalances = cashAvailableData.data.CashBalances;
    const totalUnsettledTrades =
      cashAvailableData.data.UnsettledTrades;
    const totalOutgoingsPending =
      cashAvailableData.data.OutgoingsPending;
    const totalCashAvailable = cashAvailableData.data.CashAvailable;
    const totalIncomePending = cashAvailableData.data.IncomePending;

    worksheet.addRow([
      'All',
      'Total',
      totalCashBalances,
      totalUnsettledTrades,
      totalOutgoingsPending,
      totalCashAvailable,
      totalIncomePending,
    ]).font = {
      name: 'Segoe UI',
      family: 2,
      size: 9,
      underline: false,
      bold: true,
      color: { argb: '000000' },
    };

    rowIndex++;

    for (const currencyTotal in currencyTotals) {
      if (currencyTotal) {
        const currencyTotalObject = currencyTotals[currencyTotal];
        worksheet.addRow([
          'All',
          currencyTotalObject.currencyName,
          currencyTotalObject.totalCurrencyCashBalance,
          currencyTotalObject.totalCurrencyUnsettledTrades,
          currencyTotalObject.totalCurrencyOutgoingsPending,
          currencyTotalObject.totalCurrencyCashAvailable,
          currencyTotalObject.totalCurrencyIncomePending,
        ]).font = {
          name: 'Segoe UI',
          family: 2,
          size: 9,
          underline: false,
          bold: false,
          color: { argb: '000000' },
        };
        rowIndex++;
      }
    }

    for (let j = 0; j < 7; j++) {
      const rowNumberString = rowIndex.toString();
      worksheet.getCell(this.xlsColumns[j] + rowNumberString).border = {
        bottom: { style: 'medium', color: { argb: '000000' } },
      };
    }
  }

  createCouponsSheet(
    worksheet: Excel.Worksheet,
    cashflowCouponsData: ICashflowCouponsData[]
  ) {
    let rowIndex = 0;

    worksheet.columns = [
      { header: 'Coupons and Maturities', key: '', width: 20 },
      { header: '', key: '', width: 40 },
      { header: '', key: '', width: 15 },
      { header: '', key: '', width: 10 },
      { header: '', key: '', width: 20 },
      { header: '', key: '', width: 20 },
      { header: '', key: '', width: 20 },
    ];

    const row = worksheet.getRow(1);
    row.font = {
      name: 'Segoe UI',
      family: 2,
      size: 9,
      underline: false,
      bold: true,
      color: { argb: '000000' },
    };

    rowIndex++;

    worksheet.addRow([
      '',
      'Name',
      'Date',
      'Currency',
      'Amount',
      'FX',
      'NZD',
    ]).font = {
      name: 'Segoe UI',
      family: 2,
      size: 9,
      underline: false,
      bold: true,
      color: { argb: '000000' },
    };

    rowIndex++;

    for (let i = 0; i < worksheet.columns.length; i++) {
      const cell = worksheet.getCell(this.xlsColumns[i] + rowIndex);
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: '80BD01' },
        bgColor: { argb: '80BD01' },
      };

      cell.font = {
        name: 'Segoe UI',
        family: 2,
        size: 9,
        underline: false,
        bold: true,
        color: { argb: 'ffffff' },
      };
    }

    this.createCouponsTable(worksheet, rowIndex, cashflowCouponsData);
  }

  createCouponsTable(
    worksheet: Excel.Worksheet,
    rowIndex: number,
    cashflowCouponsData: ICashflowCouponsData[]
  ) {
    for (let i = 0; i < cashflowCouponsData.length; i++) {
      const coupon = cashflowCouponsData[i];
      const type = coupon.CashflowTypeName;
      const name = coupon.Name;
      const date = new Date(coupon.Date);
      const currency = coupon.RefValue.CurrencyCode;
      const amount = this.decimalNumber(coupon.Value.Value, 2);
      const FX = this.decimalNumber(coupon.FX, 4);
      const nzd = this.decimalNumber(coupon.RefValue.Value, 2);
      worksheet.addRow([type, name, date, currency, amount, FX, nzd]).font = {
        name: 'Segoe UI',
        family: 2,
        size: 9,
        underline: false,
        bold: false,
        color: { argb: '000000' },
      };

      worksheet.getCell(rowIndex, 3).numFmt = 'dd/M/yyyy';

      rowIndex++;
    }

    for (let j = 0; j < worksheet.columns.length; j++) {
      const rowNumberString = rowIndex.toString();
      worksheet.getCell(this.xlsColumns[j] + rowNumberString).border = {
        bottom: { style: 'medium', color: { argb: '000000' } },
      };
    }
  }
}
